export type PrintMaterialLocale = 'nl' | 'de' | 'en' | 'fr';

export type PrintMaterialInfo = {
  key: string;
  name: string;
  description: string;
};

type MaterialCopy = {
  key: string;
  aliases: RegExp[];
  copy: Record<PrintMaterialLocale, { name: string; description: string }>;
};

function copy(
  nl: { name: string; description: string },
  de: { name: string; description: string },
  en: { name: string; description: string },
  fr: { name: string; description: string }
) {
  return { nl, de, en, fr };
}

const MATERIALS: MaterialCopy[] = [
  {
    key: 'carwrap-3m-ij280',
    aliases: [/carwrap[_\s-]*3m[_\s-]*ij280/i, /\b3m(?:™|tm)?\s*ij280\b/i],
    copy: copy(
      {
        name: '3M™ IJ280 gegoten witte wrapfolie',
        description:
          'Hoogwaardige gegoten printfolie voor voertuigen en vervormbare oppervlakken. De folie is dun, soepel en vrijwel krimpvrij, waardoor hij goed rond rondingen en diepe vormen verwerkt kan worden. De lijmlaag met luchtkanaaltjes helpt luchtbellen voorkomen en maakt herpositioneren tijdens montage makkelijker. Droog aanbrengen vanaf ca. 10 °C. Dikte ca. 50 micron, maximale printbreedte ca. 148 cm en verwachte levensduur tot ca. 10 jaar bij correct gebruik.',
      },
      {
        name: '3M™ IJ280 gegossene weiße Wrapfolie',
        description:
          'Hochwertige gegossene Druckfolie für Fahrzeuge und verformbare Flächen. Die Folie ist dünn, flexibel und nahezu schrumpffrei, sodass sie sich gut über Rundungen und Vertiefungen verarbeiten lässt. Die Klebeschicht mit Luftkanälen reduziert Blasen und erleichtert das Positionieren bei der Montage. Trockenverklebung ab ca. 10 °C. Stärke ca. 50 Mikron, maximale Druckbreite ca. 148 cm und erwartete Haltbarkeit bis ca. 10 Jahre bei korrekter Anwendung.',
      },
      {
        name: '3M™ IJ280 cast white wrap film',
        description:
          'High-grade cast print film for vehicles and shaped surfaces. The film is thin, flexible and almost shrink-free, making it suitable for curves and deeper recesses. The adhesive with air-release channels helps prevent bubbles and makes repositioning easier during installation. Apply dry from approx. 10 °C. Thickness approx. 50 microns, maximum print width approx. 148 cm and expected durability up to approx. 10 years when used correctly.',
      },
      {
        name: 'Film blanc coule 3M™ IJ280',
        description:
          'Film imprimé coulé haut de gamme pour véhicules et surfaces déformables. Fin, souple et presque sans retrait, il convient aux courbes et aux formes profondes. La colle avec canaux d’air limite les bulles et facilite le repositionnement pendant la pose. Pose a sec à partir d’env. 10 °C. Épaisseur env. 50 microns, largeur d’impression max. env. 148 cm et durée de vie attendue jusqu’a env. 10 ans avec une pose correcte.',
      }
    ),
  },
  {
    key: 'carwrap-avery-mpi1105',
    aliases: [/carwrap[_\s-]*avery[_\s-]*mpi1105/i, /\bavery(?:®)?\s*mpi\s*1105/i, /mpi1105/i],
    copy: copy(
      {
        name: 'Avery® MPI1105 Easy Apply™ RS gegoten witte wrapfolie',
        description:
          'Premium gegoten wrapfolie voor carwraps, bedrijfswagens en fleetmarking. De Easy Apply RS lijmlaag helpt luchtvrij monteren en maakt de folie tijdens plaatsing goed herpositioneerbaar. Geschikt voor langdurige voertuigtoepassingen en complexe vormen. Droog aanbrengen vanaf ca. 10 °C. Dikte ca. 50 micron folie, maximale printbreedte ca. 148 cm en verwachte levensduur tot ca. 10 jaar bij juiste montage.',
      },
      {
        name: 'Avery® MPI1105 Easy Apply™ RS gegossene weiße Wrapfolie',
        description:
          'Premium gegossene Wrapfolie für Carwraps, Firmenfahrzeuge und Flottenbeschriftung. Die Easy Apply RS Klebeschicht unterstützt blasenarmes Verkleben und lässt sich während der Montage gut repositionieren. Geeignet für langlebige Fahrzeuganwendungen und komplexe Formen. Trockenverklebung ab ca. 10 °C. Folienstärke ca. 50 Mikron, maximale Druckbreite ca. 148 cm und erwartete Haltbarkeit bis ca. 10 Jahre bei fachgerechter Montage.',
      },
      {
        name: 'Avery® MPI1105 Easy Apply™ RS cast white wrap film',
        description:
          'Premium cast wrap film for car wraps, commercial vehicles and fleet graphics. The Easy Apply RS adhesive supports bubble-free installation and makes the film easy to reposition during application. Suitable for long-term vehicle use and complex shapes. Apply dry from approx. 10 °C. Film thickness approx. 50 microns, maximum print width approx. 148 cm and expected durability up to approx. 10 years with professional installation.',
      },
      {
        name: 'Film blanc coule Avery® MPI1105 Easy Apply™ RS',
        description:
          'Film wrap coule premium pour carwrap, véhicules d’entreprise et marquage de flotte. La colle Easy Apply RS facilite une pose sans bulles et permet de repositionner le film pendant l’application. Convient aux usages longue durée sur véhicules et aux formes complexes. Pose a sec à partir d’env. 10 °C. Épaisseur du film env. 50 microns, largeur d’impression max. env. 148 cm et durée de vie attendue jusqu’a env. 10 ans avec une pose professionnelle.',
      }
    ),
  },
  {
    key: 'carwrap-3m-8428g',
    aliases: [/carwrap[_\s-]*gloss[_\s-]*laminate[_\s-]*3m[_\s-]*8428g/i, /\b3m(?:®)?\s*8428g\b/i],
    copy: copy(
      {
        name: '3M® 8428G gegoten glanslaminaat',
        description:
          'Glanzend gegoten laminaat voor vervormbare voertuigprints. Het beschermt de print tegen normale slijtage en geeft kleuren een heldere glans. Door de hoge vervormbaarheid past het goed bij carwrapfolie op rondingen, uitsparingen en diepe vormen. Dikte ca. 30 micron, maximale breedte ca. 148 cm en verwachte levensduur ca. 8 jaar.',
      },
      {
        name: '3M® 8428G gegossenes Glanzlaminat',
        description:
          'Glänzendes gegossenes Laminat für verformbare Fahrzeugdrucke. Es schützt den Druck vor normalem Verschleiß und gibt Farben einen klaren Glanz. Durch die hohe Verformbarkeit passt es gut zu Carwrap-Folie auf Rundungen, Vertiefungen und komplexen Formen. Stärke ca. 30 Mikron, maximale Breite ca. 148 cm und erwartete Haltbarkeit ca. 8 Jahre.',
      },
      {
        name: '3M® 8428G cast gloss laminate',
        description:
          'Gloss cast laminate for conformable vehicle prints. It protects the print against normal wear and gives colours a clear glossy finish. Its high conformability makes it a good match for wrap film on curves, recesses and complex shapes. Thickness approx. 30 microns, maximum width approx. 148 cm and expected durability approx. 8 years.',
      },
      {
        name: 'Laminat brillant coule 3M® 8428G',
        description:
          'Laminat brillant coule pour impressions véhicules deformables. Il protege l’impression contre l’usure normale et apporte une finition brillante aux couleurs. Sa grande conformabilite convient aux courbes, creux et formes complexes. Épaisseur env. 30 microns, largeur max. env. 148 cm et durée de vie attendue env. 8 ans.',
      }
    ),
  },
  {
    key: 'carwrap-3m-8550m',
    aliases: [/carwrap[_\s-]*matt[_\s-]*laminate[_\s-]*3m[_\s-]*8550m/i, /\b3m(?:®)?\s*8550m\b/i],
    copy: copy(
      {
        name: '3M® 8550M PVC-vrij mat laminaat',
        description:
          'Mat PVC-vrij laminaat voor carwraps en andere vervormbare toepassingen. Het geeft prints een rustige, minder spiegelende afwerking en beschermt tegen normale belasting tijdens gebruik. Geschikt voor rondingen en diepe uitsparingen. Dikte ca. 50 micron, maximale breedte ca. 148 cm en verwachte levensduur ca. 9 jaar.',
      },
      {
        name: '3M® 8550M PVC-freies Mattlaminat',
        description:
          'Mattes PVC-freies Laminat für Carwraps und andere verformbare Anwendungen. Es sorgt für eine ruhige, weniger spiegelnde Oberfläche und schützt den Druck bei normaler Nutzung. Geeignet für Rundungen und tiefe Vertiefungen. Stärke ca. 50 Mikron, maximale Breite ca. 148 cm und erwartete Haltbarkeit ca. 9 Jahre.',
      },
      {
        name: '3M® 8550M PVC-free matte laminate',
        description:
          'Matte PVC-free laminate for car wraps and other conformable applications. It gives prints a calm, low-reflection finish and protects them during normal use. Suitable for curves and deep recesses. Thickness approx. 50 microns, maximum width approx. 148 cm and expected durability approx. 9 years.',
      },
      {
        name: 'Laminat mat sans PVC 3M® 8550M',
        description:
          'Laminat mat sans PVC pour carwraps et autres applications deformables. Il donne un rendu sobre, peu réfléchissant, et protege l’impression lors d’un usage normal. Convient aux courbes et aux creux profonds. Épaisseur env. 50 microns, largeur max. env. 148 cm et durée de vie attendue env. 9 ans.',
      }
    ),
  },
  {
    key: 'carwrap-avery-dol1460',
    aliases: [/cast[_\s-]*gloss[_\s-]*laminate[_\s-]*avery[_\s-]*dol1460/i, /\bavery\s*dol\s*1460\b/i, /\bdol1460\b/i],
    copy: copy(
      {
        name: 'Avery DOL1460 gegoten glanslaminaat',
        description:
          'Hoogwaardig glanzend gegoten laminaat voor voertuigprints. Het verhoogt de kleurintensiteit, beschermt tegen UV, vuil en mechanische belasting en blijft vervormbaar bij complexe rondingen. Passend bij Avery wrapfolies. Dikte ca. 50 micron inclusief lijm, maximale breedte ca. 148 cm en verwachte levensduur ca. 6 jaar.',
      },
      {
        name: 'Avery DOL1460 gegossenes Glanzlaminat',
        description:
          'Hochwertiges glänzendes gegossenes Laminat für Fahrzeugdrucke. Es erhöht die Farbintensität, schützt vor UV, Schmutz und mechanischer Belastung und bleibt bei komplexen Rundungen verformbar. Passend zu Avery Wrapfolien. Stärke ca. 50 Mikron inkl. Kleber, maximale Breite ca. 148 cm und erwartete Haltbarkeit ca. 6 Jahre.',
      },
      {
        name: 'Avery DOL1460 cast gloss laminate',
        description:
          'High-quality gloss cast laminate for vehicle prints. It boosts colour intensity, protects against UV, dirt and mechanical stress, and remains conformable on complex curves. Designed to match Avery wrap films. Thickness approx. 50 microns including adhesive, maximum width approx. 148 cm and expected durability approx. 6 years.',
      },
      {
        name: 'Laminat brillant coule Avery DOL1460',
        description:
          'Laminat brillant coule haut de gamme pour impressions véhicules. Il renforce l’intensité des couleurs, protege contre les UV, la salete et les contraintes mecaniques, tout en restant deformable sur formes complexes. Adapte aux films wrap Avery. Épaisseur env. 50 microns colle comprise, largeur max. env. 148 cm et durée de vie attendue env. 6 ans.',
      }
    ),
  },
  {
    key: 'carwrap-avery-dol1480',
    aliases: [/cast[_\s-]*(mat|matt)[_\s-]*laminate[_\s-]*avery[_\s-]*dol1480/i, /\bavery\s*dol\s*1480\b/i, /\bdol1480\b/i],
    copy: copy(
      {
        name: 'Avery DOL1480 gegoten mat laminaat',
        description:
          'Mat gegoten laminaat voor hoogwaardige voertuigprints. Het vermindert reflectie, beschermt tegen UV, vuil en lichte mechanische belasting en blijft goed vervormbaar bij rondingen en uitsparingen. Passend bij Avery wrapfolies. Dikte ca. 50 micron inclusief lijm, maximale breedte ca. 148 cm en verwachte levensduur ca. 6 jaar.',
      },
      {
        name: 'Avery DOL1480 gegossenes Mattlaminat',
        description:
          'Mattes gegossenes Laminat für hochwertige Fahrzeugdrucke. Es reduziert Reflexionen, schützt vor UV, Schmutz und leichter mechanischer Belastung und bleibt auf Rundungen und Vertiefungen gut verformbar. Passend zu Avery Wrapfolien. Stärke ca. 50 Mikron inkl. Kleber, maximale Breite ca. 148 cm und erwartete Haltbarkeit ca. 6 Jahre.',
      },
      {
        name: 'Avery DOL1480 cast matte laminate',
        description:
          'Matte cast laminate for high-quality vehicle prints. It reduces reflection, protects against UV, dirt and light mechanical stress, and remains conformable on curves and recesses. Designed to match Avery wrap films. Thickness approx. 50 microns including adhesive, maximum width approx. 148 cm and expected durability approx. 6 years.',
      },
      {
        name: 'Laminat mat coule Avery DOL1480',
        description:
          'Laminat mat coule pour impressions véhicules haut de gamme. Il reduit les reflets, protege contre les UV, la salete et les contraintes mecaniques légères, tout en restant deformable sur courbes et creux. Adapte aux films wrap Avery. Épaisseur env. 50 microns colle comprise, largeur max. env. 148 cm et durée de vie attendue env. 6 ans.',
      }
    ),
  },
  // Reclamebord plaatmaterialen. Staan bewust vóór de Forex-entries: de
  // gerecyclede varianten mogen nooit op de gewone Forex-teksten vallen.
  {
    key: 'gerecycled-forex-3mm',
    aliases: [
      /\b(?:recycled|gerecycled|recyceltes)\s*forex(?:®)?\s*3\s*mm\b/i,
      /\bforex(?:®)?\s*recycl[ée]\s*3\s*mm\b/i,
    ],
    copy: copy(
      {
        name: 'Gerecycled Forex® 3 mm',
        description:
          'Gerecycled Forex® is een plaat van geschuimd PVC waarin 30 procent gerecycled materiaal is verwerkt, afkomstig van snijafval en gebruikte Forex®. Zo kies je voor een duurzamere plaat die net als gewone Forex® licht van gewicht is. De plaat wordt bedrukt met UV-inkt en is geschikt voor binnen en voor kortere tijd buiten, met een verwachte levensduur van 2 tot 5 jaar. Dikte 3 mm, gewicht ca. 1,53 kg/m2.',
      },
      {
        name: 'Recyceltes Forex® 3 mm',
        description:
          'Recyceltes Forex® ist eine Platte aus geschäumtem PVC mit 30 Prozent recyceltem Material aus Schnittresten und gebrauchtem Forex®. So wählen Sie eine nachhaltigere Platte, die wie normales Forex® ein geringes Gewicht hat. Die Platte wird mit UV-Tinte bedruckt und eignet sich für innen und für kürzere Zeit außen, mit einer erwarteten Lebensdauer von 2 bis 5 Jahren. Stärke 3 mm, Gewicht ca. 1,53 kg/m2.',
      },
      {
        name: 'Recycled Forex® 3 mm',
        description:
          'Recycled Forex® is a foamed PVC sheet containing 30 percent recycled material, made from cutting waste and used Forex®. It is a more sustainable choice that is just as lightweight as standard Forex®. The sheet is printed with UV ink and is suitable for indoor use and short-term outdoor use, with an expected lifespan of 2 to 5 years. Thickness 3 mm, weight approx. 1.53 kg/m2.',
      },
      {
        name: 'Forex® recyclé 3 mm',
        description:
          'Le Forex® recyclé est une plaque en PVC expansé contenant 30 % de matière recyclée, issue de chutes de découpe et de Forex® usagé. Vous choisissez ainsi une plaque plus durable, aussi légère que le Forex® classique. La plaque est imprimée avec des encres UV et convient à l’intérieur ainsi qu’à un usage extérieur de courte durée, avec une durée de vie prévue de 2 à 5 ans. Épaisseur 3 mm, poids env. 1,53 kg/m2.',
      }
    ),
  },
  {
    key: 'gerecycled-forex-5mm',
    aliases: [
      /\b(?:recycled|gerecycled|recyceltes)\s*forex(?:®)?\s*5\s*mm\b/i,
      /\bforex(?:®)?\s*recycl[ée]\s*5\s*mm\b/i,
    ],
    copy: copy(
      {
        name: 'Gerecycled Forex® 5 mm',
        description:
          'Gerecycled Forex® is een plaat van geschuimd PVC waarin 30 procent gerecycled materiaal is verwerkt, afkomstig van snijafval en gebruikte Forex®. Deze 5 mm plaat is dikker en steviger dan de 3 mm uitvoering. De plaat wordt bedrukt met UV-inkt en is geschikt voor binnen en voor kortere tijd buiten, met een verwachte levensduur van 2 tot 5 jaar. Dikte 5 mm, gewicht ca. 2,48 kg/m2.',
      },
      {
        name: 'Recyceltes Forex® 5 mm',
        description:
          'Recyceltes Forex® ist eine Platte aus geschäumtem PVC mit 30 Prozent recyceltem Material aus Schnittresten und gebrauchtem Forex®. Diese 5-mm-Platte ist dicker und stabiler als die 3-mm-Ausführung. Die Platte wird mit UV-Tinte bedruckt und eignet sich für innen und für kürzere Zeit außen, mit einer erwarteten Lebensdauer von 2 bis 5 Jahren. Stärke 5 mm, Gewicht ca. 2,48 kg/m2.',
      },
      {
        name: 'Recycled Forex® 5 mm',
        description:
          'Recycled Forex® is a foamed PVC sheet containing 30 percent recycled material, made from cutting waste and used Forex®. This 5 mm sheet is thicker and sturdier than the 3 mm version. It is printed with UV ink and is suitable for indoor use and short-term outdoor use, with an expected lifespan of 2 to 5 years. Thickness 5 mm, weight approx. 2.48 kg/m2.',
      },
      {
        name: 'Forex® recyclé 5 mm',
        description:
          'Le Forex® recyclé est une plaque en PVC expansé contenant 30 % de matière recyclée, issue de chutes de découpe et de Forex® usagé. Cette plaque de 5 mm est plus épaisse et plus rigide que la version de 3 mm. Elle est imprimée avec des encres UV et convient à l’intérieur ainsi qu’à un usage extérieur de courte durée, avec une durée de vie prévue de 2 à 5 ans. Épaisseur 5 mm, poids env. 2,48 kg/m2.',
      }
    ),
  },
  {
    key: 'forex-2mm',
    aliases: [/\bforex(?:®)?\s*2\s*mm\b/i, /\b2\s*mm\s*forex\b/i],
    copy: copy(
      {
        name: 'Forex® 2 mm',
        description:
          'Forex® is gemaakt van geschuimd PVC. Het materiaal is licht van gewicht, heeft een witte ondergrond en wordt bedrukt met UV-inkt. Met 2 mm zit deze plaat qua dikte tussen de dunne 1 mm en de stevigere 3 mm uitvoering in. Geschikt voor binnen en voor kortere tijd buiten, met een verwachte levensduur van 2 tot 5 jaar. Brandklasse C-s3,d0 volgens EN 13501. Dikte 2 mm.',
      },
      {
        name: 'Forex® 2 mm',
        description:
          'Forex® besteht aus geschäumtem PVC. Das Material ist leicht, hat eine weiße Oberfläche und wird mit UV-Tinte bedruckt. Mit 2 mm liegt diese Platte in der Stärke zwischen der dünnen 1-mm- und der stabileren 3-mm-Ausführung. Geeignet für innen und für kürzere Zeit außen, mit einer erwarteten Lebensdauer von 2 bis 5 Jahren. Brandklasse C-s3,d0 nach EN 13501. Stärke 2 mm.',
      },
      {
        name: 'Forex® 2 mm',
        description:
          'Forex® is made from foamed PVC. It is lightweight, has a white surface and is printed with UV ink. At 2 mm, this sheet sits between the thin 1 mm and the sturdier 3 mm version. Suitable for indoor use and short-term outdoor use, with an expected lifespan of 2 to 5 years. Fire classification C-s3,d0 according to EN 13501. Thickness 2 mm.',
      },
      {
        name: 'Forex® 2 mm',
        description:
          'Le Forex® est fabriqué en PVC expansé. Le matériau est léger, présente une surface blanche et est imprimé avec des encres UV. Avec 2 mm, cette plaque se situe entre la version fine de 1 mm et la version plus rigide de 3 mm. Convient à l’intérieur et à un usage extérieur de courte durée, avec une durée de vie prévue de 2 à 5 ans. Classement au feu C-s3,d0 selon EN 13501. Épaisseur 2 mm.',
      }
    ),
  },
  {
    key: 'kanaalplaat-3-5mm',
    aliases: [/\bpolypropylene\s*35\s*mm\b/i, /\bkanaalplaat\s*3[,.]5\s*mm\b/i],
    copy: copy(
      {
        name: 'Kanaalplaat 3,5 mm',
        description:
          'Kanaalplaat is gemaakt van polypropyleen met een holle kanaalstructuur. Daardoor is de plaat erg licht en toch stijf, en je kunt hem insnijden zodat hij te vouwen is. Het materiaal is PVC-vrij, wordt bedrukt met UV-inkt en is geschikt voor binnen en buiten. De verwachte levensduur is kort, minder dan 2 jaar, dus vooral geschikt voor tijdelijk gebruik. Zonder brandklasse. Dikte 3,5 mm, gewicht ca. 0,65 kg/m2.',
      },
      {
        name: 'Hohlkammerplatte 3,5 mm',
        description:
          'Die Hohlkammerplatte besteht aus Polypropylen mit einer hohlen Kanalstruktur. Dadurch ist sie sehr leicht und dennoch steif, und Sie können sie einritzen, um sie zu falten. Das Material ist PVC-frei, wird mit UV-Tinte bedruckt und eignet sich für innen und außen. Die erwartete Lebensdauer ist kurz, unter 2 Jahren, daher vor allem für den zeitlich begrenzten Einsatz geeignet. Ohne Brandklasse. Stärke 3,5 mm, Gewicht ca. 0,65 kg/m2.',
      },
      {
        name: 'Fluted polypropylene 3.5 mm',
        description:
          'Fluted polypropylene is a polypropylene sheet with a hollow channel structure. This makes it very light yet stiff, and it can be scored so that it folds. The material is PVC-free, printed with UV ink and suitable for indoor and outdoor use. The expected lifespan is short, under 2 years, so it is best suited to temporary use. No fire classification. Thickness 3.5 mm, weight approx. 0.65 kg/m2.',
      },
      {
        name: 'Polypropylène alvéolaire 3,5 mm',
        description:
          'Le polypropylène alvéolaire est une plaque en polypropylène à structure creuse en canaux. Elle est donc très légère tout en restant rigide, et vous pouvez l’inciser pour la plier. Le matériau est sans PVC, imprimé avec des encres UV et convient à l’intérieur comme à l’extérieur. Sa durée de vie prévue est courte, moins de 2 ans, il convient donc surtout à un usage temporaire. Sans classement au feu. Épaisseur 3,5 mm, poids env. 0,65 kg/m2.',
      }
    ),
  },
  {
    key: 'kanaalplaat-8mm',
    aliases: [/\bpolypropylene\s*8\s*mm\b/i, /\bkanaalplaat\s*8\s*mm\b/i],
    copy: copy(
      {
        name: 'Kanaalplaat 8 mm',
        description:
          'Kanaalplaat is gemaakt van polypropyleen met een holle kanaalstructuur, waardoor de plaat licht blijft en toch stijf is. Deze 8 mm uitvoering is dikker en steviger dan de 3,5 mm plaat en gaat ook langer mee, met een verwachte levensduur van 2 tot 5 jaar. Het materiaal is PVC-vrij, wordt bedrukt met UV-inkt en is geschikt voor binnen en buiten. Zonder brandklasse. Dikte 8 mm, gewicht ca. 1,4 kg/m2.',
      },
      {
        name: 'Hohlkammerplatte 8 mm',
        description:
          'Die Hohlkammerplatte besteht aus Polypropylen mit einer hohlen Kanalstruktur, wodurch sie leicht und dennoch steif ist. Diese 8-mm-Ausführung ist dicker und stabiler als die 3,5-mm-Platte und hält auch länger, mit einer erwarteten Lebensdauer von 2 bis 5 Jahren. Das Material ist PVC-frei, wird mit UV-Tinte bedruckt und eignet sich für innen und außen. Ohne Brandklasse. Stärke 8 mm, Gewicht ca. 1,4 kg/m2.',
      },
      {
        name: 'Fluted polypropylene 8 mm',
        description:
          'Fluted polypropylene is a polypropylene sheet with a hollow channel structure, which keeps it light yet stiff. This 8 mm version is thicker and sturdier than the 3.5 mm sheet and also lasts longer, with an expected lifespan of 2 to 5 years. The material is PVC-free, printed with UV ink and suitable for indoor and outdoor use. No fire classification. Thickness 8 mm, weight approx. 1.4 kg/m2.',
      },
      {
        name: 'Polypropylène alvéolaire 8 mm',
        description:
          'Le polypropylène alvéolaire est une plaque en polypropylène à structure creuse en canaux, ce qui la rend légère tout en restant rigide. Cette version de 8 mm est plus épaisse et plus rigide que la plaque de 3,5 mm et dure plus longtemps, avec une durée de vie prévue de 2 à 5 ans. Le matériau est sans PVC, imprimé avec des encres UV et convient à l’intérieur comme à l’extérieur. Sans classement au feu. Épaisseur 8 mm, poids env. 1,4 kg/m2.',
      }
    ),
  },
  {
    key: 'akylite-2-9mm',
    aliases: [/\bakylite(?:®)?\s*(?:29\s*mm|2[,.]9\s*mm)\b/i],
    copy: copy(
      {
        name: 'Akylite® 2,9 mm',
        description:
          'Akylite® is een plaat van polypropyleen met een transparante kern die tot een honingraatstructuur is gevormd. Daardoor is de plaat heel licht en toch stevig. Het materiaal is PVC-vrij, waterafstotend en geschikt voor binnen en buiten, en wordt enkelzijdig bedrukt met UV-inkt. Akylite® is niet brandvertragend en dus niet geschikt voor openbare ruimtes. De verwachte levensduur is kort, minder dan 2 jaar. Dikte 2,9 mm, gewicht ca. 0,6 kg/m2.',
      },
      {
        name: 'Akylite® 2,9 mm',
        description:
          'Akylite® ist eine Platte aus Polypropylen mit einem transparenten Kern in Wabenstruktur. Dadurch ist die Platte sehr leicht und trotzdem stabil. Das Material ist PVC-frei, wasserabweisend und für innen und außen geeignet und wird einseitig mit UV-Tinte bedruckt. Akylite® ist nicht schwer entflammbar und daher nicht für öffentliche Räume geeignet. Die erwartete Lebensdauer ist kurz, unter 2 Jahren. Stärke 2,9 mm, Gewicht ca. 0,6 kg/m2.',
      },
      {
        name: 'Akylite® 2.9 mm',
        description:
          'Akylite® is a polypropylene sheet with a transparent core formed into a honeycomb structure. This makes the sheet very light yet sturdy. The material is PVC-free, water-repellent and suitable for indoor and outdoor use, and is printed single-sided with UV ink. Akylite® is not flame-retardant, so it is not suitable for public spaces. The expected lifespan is short, under 2 years. Thickness 2.9 mm, weight approx. 0.6 kg/m2.',
      },
      {
        name: 'Akylite® 2,9 mm',
        description:
          'Akylite® est une plaque en polypropylène dotée d’un cœur transparent formé en structure alvéolaire en nid d’abeille. La plaque est donc très légère tout en restant solide. Le matériau est sans PVC, hydrofuge, adapté à l’intérieur et à l’extérieur, et imprimé en recto seul avec des encres UV. Akylite® n’est pas ignifuge et ne convient donc pas aux espaces publics. Sa durée de vie prévue est courte, moins de 2 ans. Épaisseur 2,9 mm, poids env. 0,6 kg/m2.',
      }
    ),
  },
  {
    key: 'dibond-budget-3mm',
    aliases: [/\bdibond(?:®)?\s*budget\s*3\s*mm\b/i],
    copy: copy(
      {
        name: 'Dibond Budget 3 mm',
        description:
          'Dibond Budget is een aluminium sandwichpaneel van 3 mm, met aluminium buitenlagen en een zwarte kunststof kern. De plaat is licht, weerbestendig, brandvertragend en trekt niet krom. Als voordeligere variant is hij wel minder stabiel dan standaard Dibond®: hij raakt sneller beschadigd, is gevoeliger voor omgevingsinvloeden en tussen productiebatches kan kleurverschil optreden. Een goede keuze als je een stevig aluminium bord wilt voor een scherpere prijs. Dikte 3 mm.',
      },
      {
        name: 'Dibond Budget 3 mm',
        description:
          'Dibond Budget ist eine 3 mm starke Aluminium-Sandwichplatte mit Deckschichten aus Aluminium und einem schwarzen Kunststoffkern. Die Platte ist leicht, witterungsbeständig, schwer entflammbar und verzieht sich nicht. Als günstigere Variante ist sie jedoch weniger stabil als Standard-Dibond®: Sie wird schneller beschädigt, reagiert empfindlicher auf Umgebungseinflüsse und zwischen Produktionschargen kann es Farbunterschiede geben. Eine gute Wahl, wenn Sie ein stabiles Aluminiumschild zu einem günstigeren Preis möchten. Stärke 3 mm.',
      },
      {
        name: 'Dibond Budget 3 mm',
        description:
          'Dibond Budget is a 3 mm aluminium sandwich panel with aluminium outer layers and a black plastic core. The sheet is light, weather-resistant, flame-retardant and does not warp. As the more affordable option it is less stable than standard Dibond®: it is more easily damaged, more sensitive to environmental conditions, and colour can vary between production batches. A good choice if you want a sturdy aluminium sign at a lower price. Thickness 3 mm.',
      },
      {
        name: 'Dibond Budget 3 mm',
        description:
          'Dibond Budget est un panneau sandwich en aluminium de 3 mm, avec des couches extérieures en aluminium et un cœur noir en plastique. La plaque est légère, résistante aux intempéries, ignifuge et ne se déforme pas. En tant que variante plus économique, elle est toutefois moins stable que le Dibond® standard : elle s’abîme plus facilement, est plus sensible aux conditions ambiantes et la couleur peut varier d’un lot de production à l’autre. Un bon choix pour un panneau en aluminium solide à un prix plus doux. Épaisseur 3 mm.',
      }
    ),
  },
  {
    key: 'dibond-4mm',
    aliases: [/\bdibond\s*budget\s*4\s*mm\b/i, /\bdibond®\s*4\s*mm\b/i],
    copy: copy(
      {
        name: 'Dibond® 4 mm',
        description:
          'Dibond® is een aluminium composietplaat, opgebouwd uit aluminium en polyethyleen. De plaat wordt direct met UV-inkt bedrukt, is PVC-vrij en geschikt voor binnen en buiten. Met 4 mm is deze plaat dikker en daardoor stijver dan de standaard 3 mm uitvoering, handig als je een extra stevig bord wilt dat zijn vorm goed vasthoudt. Dikte 4 mm.',
      },
      {
        name: 'Dibond® 4 mm',
        description:
          'Dibond® ist eine Aluminium-Verbundplatte aus Aluminium und Polyethylen. Die Platte wird direkt mit UV-Tinte bedruckt, ist PVC-frei und für innen und außen geeignet. Mit 4 mm ist diese Platte dicker und dadurch steifer als die Standardausführung mit 3 mm, praktisch, wenn Sie ein besonders stabiles Schild möchten, das gut in Form bleibt. Stärke 4 mm.',
      },
      {
        name: 'Dibond® 4 mm',
        description:
          'Dibond® is an aluminium composite sheet made of aluminium and polyethylene. It is printed directly with UV ink, is PVC-free and suitable for indoor and outdoor use. At 4 mm this sheet is thicker and therefore stiffer than the standard 3 mm version, useful when you want an extra sturdy sign that holds its shape well. Thickness 4 mm.',
      },
      {
        name: 'Dibond® 4 mm',
        description:
          'Le Dibond® est une plaque composite en aluminium, composée d’aluminium et de polyéthylène. Elle est imprimée directement avec des encres UV, sans PVC et adaptée à l’intérieur comme à l’extérieur. Avec 4 mm, cette plaque est plus épaisse et donc plus rigide que la version standard de 3 mm, idéale si vous voulez un panneau extra solide qui garde bien sa forme. Épaisseur 4 mm.',
      }
    ),
  },
  {
    key: 'dibond-6mm',
    aliases: [/\bdibond\s*budget\s*6\s*mm\b/i, /\bdibond®\s*6\s*mm\b/i],
    copy: copy(
      {
        name: 'Dibond® 6 mm',
        description:
          'Dibond® is een aluminium composietplaat, opgebouwd uit aluminium en polyethyleen. De plaat wordt direct met UV-inkt bedrukt, is PVC-vrij en geschikt voor binnen en buiten. Met 6 mm is dit de dikste en stijfste Dibond® plaat in dit assortiment, ruim steviger dan de standaard 3 mm uitvoering. Kies deze dikte als je een zeer robuust bord wilt dat zijn vorm goed vasthoudt. Dikte 6 mm.',
      },
      {
        name: 'Dibond® 6 mm',
        description:
          'Dibond® ist eine Aluminium-Verbundplatte aus Aluminium und Polyethylen. Die Platte wird direkt mit UV-Tinte bedruckt, ist PVC-frei und für innen und außen geeignet. Mit 6 mm ist dies die dickste und steifste Dibond®-Platte in diesem Sortiment, deutlich stabiler als die Standardausführung mit 3 mm. Wählen Sie diese Stärke, wenn Sie ein sehr robustes Schild möchten, das gut in Form bleibt. Stärke 6 mm.',
      },
      {
        name: 'Dibond® 6 mm',
        description:
          'Dibond® is an aluminium composite sheet made of aluminium and polyethylene. It is printed directly with UV ink, is PVC-free and suitable for indoor and outdoor use. At 6 mm this is the thickest and stiffest Dibond® sheet in this range, considerably sturdier than the standard 3 mm version. Choose this thickness when you want a very robust sign that holds its shape well. Thickness 6 mm.',
      },
      {
        name: 'Dibond® 6 mm',
        description:
          'Le Dibond® est une plaque composite en aluminium, composée d’aluminium et de polyéthylène. Elle est imprimée directement avec des encres UV, sans PVC et adaptée à l’intérieur comme à l’extérieur. Avec 6 mm, c’est la plaque Dibond® la plus épaisse et la plus rigide de cette gamme, nettement plus solide que la version standard de 3 mm. Choisissez cette épaisseur pour un panneau très robuste qui garde bien sa forme. Épaisseur 6 mm.',
      }
    ),
  },
  {
    key: 'dibond-digitaal-3mm',
    aliases: [/\bdibond(?:®)?\s*(?:digitaal|digital|numérique)\s*3\s*mm\b/i],
    copy: copy(
      {
        name: 'Dibond® digitaal 3 mm',
        description:
          'Dibond® digitaal is een aluminium composietplaat van aluminium en polyethyleen, die direct digitaal met UV-inkt wordt bedrukt. De plaat is PVC-vrij, geschikt voor binnen en buiten en heeft een verwachte levensduur van 2 tot 5 jaar. Je kiest voor enkel- of dubbelzijdig drukken en kunt de print extra beschermen met een matte, glanzende of anti-graffiti beschermlaag. Brandklasse D-s2,d0 volgens EN 13501. Dikte 3 mm, gewicht ca. 3,5 kg/m2.',
      },
      {
        name: 'Dibond® Digital 3 mm',
        description:
          'Dibond® Digital ist eine Aluminium-Verbundplatte aus Aluminium und Polyethylen, die direkt digital mit UV-Tinte bedruckt wird. Die Platte ist PVC-frei, für innen und außen geeignet und hat eine erwartete Lebensdauer von 2 bis 5 Jahren. Sie wählen einseitigen oder doppelseitigen Druck und können den Druck zusätzlich mit einer matten, glänzenden oder Anti-Graffiti-Schutzschicht schützen. Brandklasse D-s2,d0 nach EN 13501. Stärke 3 mm, Gewicht ca. 3,5 kg/m2.',
      },
      {
        name: 'Dibond® digital 3 mm',
        description:
          'Dibond® digital is an aluminium composite sheet made of aluminium and polyethylene, printed directly and digitally with UV ink. The sheet is PVC-free, suitable for indoor and outdoor use and has an expected lifespan of 2 to 5 years. You can choose single- or double-sided printing and add extra protection with a matt, gloss or anti-graffiti protective coating. Fire classification D-s2,d0 according to EN 13501. Thickness 3 mm, weight approx. 3.5 kg/m2.',
      },
      {
        name: 'Dibond® numérique 3 mm',
        description:
          'Le Dibond® numérique est une plaque composite en aluminium et polyéthylène, imprimée directement en numérique avec des encres UV. La plaque est sans PVC, adaptée à l’intérieur comme à l’extérieur et a une durée de vie prévue de 2 à 5 ans. Vous choisissez une impression recto ou recto verso et pouvez protéger davantage l’impression avec un revêtement mat, brillant ou anti-graffiti. Classement au feu D-s2,d0 selon EN 13501. Épaisseur 3 mm, poids env. 3,5 kg/m2.',
      }
    ),
  },
  {
    key: 'dibond-premium-3mm',
    aliases: [/\bdibond(?:®)?\s*premium\s*3\s*mm\b/i],
    copy: copy(
      {
        name: 'Dibond® premium 3 mm',
        description:
          'Dibond® premium is een aluminium composietplaat van 3 mm, opgebouwd uit aluminium en polyethyleen en direct met UV-inkt bedrukt. De plaat is PVC-vrij en geschikt voor binnen en buiten. Wil je de print extra beschermen, dan kies je voor een matte, glanzende of anti-graffiti beschermlaag. Dikte 3 mm.',
      },
      {
        name: 'Dibond® Premium 3 mm',
        description:
          'Dibond® Premium ist eine 3 mm starke Aluminium-Verbundplatte aus Aluminium und Polyethylen, die direkt mit UV-Tinte bedruckt wird. Die Platte ist PVC-frei und für innen und außen geeignet. Wenn Sie den Druck zusätzlich schützen möchten, wählen Sie eine matte, glänzende oder Anti-Graffiti-Schutzschicht. Stärke 3 mm.',
      },
      {
        name: 'Dibond® premium 3 mm',
        description:
          'Dibond® premium is a 3 mm aluminium composite sheet made of aluminium and polyethylene and printed directly with UV ink. The sheet is PVC-free and suitable for indoor and outdoor use. If you want extra protection for the print, choose a matt, gloss or anti-graffiti protective coating. Thickness 3 mm.',
      },
      {
        name: 'Dibond® premium 3 mm',
        description:
          'Le Dibond® premium est une plaque composite en aluminium de 3 mm, composée d’aluminium et de polyéthylène et imprimée directement avec des encres UV. La plaque est sans PVC et adaptée à l’intérieur comme à l’extérieur. Pour protéger davantage l’impression, choisissez un revêtement mat, brillant ou anti-graffiti. Épaisseur 3 mm.',
      }
    ),
  },
  {
    key: 'dibond-xl-3mm',
    aliases: [/\bdibond(?:®)?\s*xl\s*3\s*mm\b/i],
    copy: copy(
      {
        name: 'Dibond® XL 3 mm',
        description:
          'Dibond® XL is een aluminium composietplaat van aluminium en polyethyleen in een extra groot plaatformaat, zodat grote borden uit één stuk gemaakt kunnen worden. De plaat wordt met UV-inkt bedrukt, is PVC-vrij en geschikt voor binnen en buiten, met een verwachte levensduur van 2 tot 5 jaar. Brandklasse D-s2,d0 volgens EN 13501. Dikte 3 mm, gewicht ca. 4,32 kg/m2.',
      },
      {
        name: 'Dibond® XL 3 mm',
        description:
          'Dibond® XL ist eine Aluminium-Verbundplatte aus Aluminium und Polyethylen in einem extra großen Plattenformat, sodass große Schilder aus einem Stück gefertigt werden können. Die Platte wird mit UV-Tinte bedruckt, ist PVC-frei und für innen und außen geeignet, mit einer erwarteten Lebensdauer von 2 bis 5 Jahren. Brandklasse D-s2,d0 nach EN 13501. Stärke 3 mm, Gewicht ca. 4,32 kg/m2.',
      },
      {
        name: 'Dibond® XL 3 mm',
        description:
          'Dibond® XL is an aluminium composite sheet made of aluminium and polyethylene in an extra large sheet size, so large signs can be made from a single piece. It is printed with UV ink, is PVC-free and suitable for indoor and outdoor use, with an expected lifespan of 2 to 5 years. Fire classification D-s2,d0 according to EN 13501. Thickness 3 mm, weight approx. 4.32 kg/m2.',
      },
      {
        name: 'Dibond® XL 3 mm',
        description:
          'Le Dibond® XL est une plaque composite en aluminium et polyéthylène dans un très grand format de plaque, permettant de réaliser de grands panneaux d’une seule pièce. Elle est imprimée avec des encres UV, sans PVC et adaptée à l’intérieur comme à l’extérieur, avec une durée de vie prévue de 2 à 5 ans. Classement au feu D-s2,d0 selon EN 13501. Épaisseur 3 mm, poids env. 4,32 kg/m2.',
      }
    ),
  },
  {
    key: 'easyprint-10mm',
    aliases: [/\beasyprint\s*10\s*mm\b/i],
    copy: copy(
      {
        name: 'EasyPrint 10 mm',
        description:
          'EasyPrint is een plaat van 10 mm met een kern van wit geschuimd polystyreen en toplagen van wit polypropyleen met een matte uitstraling. De plaat is weerbestendig, waterafstotend, PVC-vrij en geschikt voor binnen en buiten, met een verwachte levensduur van 2 tot 5 jaar. EasyPrint is niet brandvertragend en daarom niet geschikt voor openbare ruimtes. Dikte 10 mm, gewicht ca. 2,13 kg/m2.',
      },
      {
        name: 'EasyPrint 10 mm',
        description:
          'EasyPrint ist eine 10 mm starke Platte mit einem Kern aus weißem geschäumtem Polystyrol und Deckschichten aus weißem Polypropylen mit matter Optik. Die Platte ist witterungsbeständig, wasserabweisend, PVC-frei und für innen und außen geeignet, mit einer erwarteten Lebensdauer von 2 bis 5 Jahren. EasyPrint ist nicht schwer entflammbar und daher nicht für öffentliche Räume geeignet. Stärke 10 mm, Gewicht ca. 2,13 kg/m2.',
      },
      {
        name: 'EasyPrint 10 mm',
        description:
          'EasyPrint is a 10 mm sheet with a core of white foamed polystyrene and white polypropylene top layers with a matt finish. The sheet is weather-resistant, water-repellent, PVC-free and suitable for indoor and outdoor use, with an expected lifespan of 2 to 5 years. EasyPrint is not flame-retardant, so it is not suitable for public spaces. Thickness 10 mm, weight approx. 2.13 kg/m2.',
      },
      {
        name: 'EasyPrint 10 mm',
        description:
          'EasyPrint est une plaque de 10 mm avec un cœur en polystyrène expansé blanc et des couches extérieures en polypropylène blanc à l’aspect mat. La plaque résiste aux intempéries, est hydrofuge, sans PVC et adaptée à l’intérieur comme à l’extérieur, avec une durée de vie prévue de 2 à 5 ans. EasyPrint n’est pas ignifuge et ne convient donc pas aux espaces publics. Épaisseur 10 mm, poids env. 2,13 kg/m2.',
      }
    ),
  },
  {
    key: 'hpl-glans',
    aliases: [/\bhpl\s*gloss\b/i, /\bhpl\s*(?:glans|glänzend|brillant)\b/i],
    copy: copy(
      {
        name: 'HPL glans',
        description:
          'Bij HPL wordt je ontwerp gedrukt op PVC-folie met een permanente lijmlaag, die daarna op een HPL-plaat van 6 mm wordt geplakt. Zo krijg je een stevig, compact bord dat geschikt is voor binnen en buiten, met een verwachte levensduur van 2 tot 5 jaar. Deze uitvoering heeft een glanzende uitstraling, met dezelfde opbouw als de matte variant. Zonder brandklasse. Dikte van de plaat 6 mm.',
      },
      {
        name: 'HPL glänzend',
        description:
          'Bei HPL wird Ihr Motiv auf PVC-Folie mit permanenter Klebeschicht gedruckt und anschließend auf eine 6 mm starke HPL-Platte geklebt. So erhalten Sie ein stabiles, kompaktes Schild für innen und außen mit einer erwarteten Lebensdauer von 2 bis 5 Jahren. Diese Ausführung hat eine glänzende Optik, bei gleichem Aufbau wie die matte Variante. Ohne Brandklasse. Plattenstärke 6 mm.',
      },
      {
        name: 'HPL gloss',
        description:
          'With HPL, your design is printed on PVC film with a permanent adhesive layer, which is then mounted on a 6 mm HPL panel. The result is a sturdy, compact sign suitable for indoor and outdoor use, with an expected lifespan of 2 to 5 years. This version has a glossy look, with the same build as the matt version. No fire classification. Panel thickness 6 mm.',
      },
      {
        name: 'HPL brillant',
        description:
          'Avec le HPL, votre visuel est imprimé sur un film PVC à couche adhésive permanente, ensuite collé sur une plaque HPL de 6 mm. Vous obtenez ainsi un panneau solide et compact, adapté à l’intérieur comme à l’extérieur, avec une durée de vie prévue de 2 à 5 ans. Cette version a un aspect brillant, avec la même composition que la version mate. Sans classement au feu. Épaisseur de la plaque 6 mm.',
      }
    ),
  },
  {
    key: 'hpl-mat',
    aliases: [/\bhpl\s*matt?\b/i],
    copy: copy(
      {
        name: 'HPL mat',
        description:
          'Bij HPL wordt je ontwerp gedrukt op PVC-folie met een permanente lijmlaag, die daarna op een HPL-plaat van 6 mm wordt geplakt. Zo krijg je een stevig, compact bord dat geschikt is voor binnen en buiten, met een verwachte levensduur van 2 tot 5 jaar. Deze uitvoering heeft een matte uitstraling, met dezelfde opbouw als de glanzende variant. Zonder brandklasse. Dikte van de plaat 6 mm.',
      },
      {
        name: 'HPL matt',
        description:
          'Bei HPL wird Ihr Motiv auf PVC-Folie mit permanenter Klebeschicht gedruckt und anschließend auf eine 6 mm starke HPL-Platte geklebt. So erhalten Sie ein stabiles, kompaktes Schild für innen und außen mit einer erwarteten Lebensdauer von 2 bis 5 Jahren. Diese Ausführung hat eine matte Optik, bei gleichem Aufbau wie die glänzende Variante. Ohne Brandklasse. Plattenstärke 6 mm.',
      },
      {
        name: 'HPL matt',
        description:
          'With HPL, your design is printed on PVC film with a permanent adhesive layer, which is then mounted on a 6 mm HPL panel. The result is a sturdy, compact sign suitable for indoor and outdoor use, with an expected lifespan of 2 to 5 years. This version has a matt look, with the same build as the gloss version. No fire classification. Panel thickness 6 mm.',
      },
      {
        name: 'HPL mat',
        description:
          'Avec le HPL, votre visuel est imprimé sur un film PVC à couche adhésive permanente, ensuite collé sur une plaque HPL de 6 mm. Vous obtenez ainsi un panneau solide et compact, adapté à l’intérieur comme à l’extérieur, avec une durée de vie prévue de 2 à 5 ans. Cette version a un aspect mat, avec la même composition que la version brillante. Sans classement au feu. Épaisseur de la plaque 6 mm.',
      }
    ),
  },
  {
    key: 'sign-again-2mm-recycled',
    aliases: [/\bsign\s*again(?:®)?\s*2\s*mm\s*(?:recycled|recycle-look|recyclingoptik|aspect\s*recycl)/i],
    copy: copy(
      {
        name: 'Sign Again® 2 mm recycle-look',
        description:
          'Sign Again® is een plaat van 100% gerecycled materiaal, gemaakt van gerecycled bannermateriaal. De plaat is sterk, slag- en krasvast en toch buigzaam, brandvertragend volgens EN 13501 (D-s3,d2) en daardoor ook geschikt voor openbare ruimtes. Geschikt voor binnen en buiten, met een verwachte levensduur van 2 tot 5 jaar. Deze uitvoering heeft een onbehandelde recycle-look; een matte of glanzende beschermlaag is optioneel. Dikte 2 mm, gewicht ca. 2,8 kg/m2, maximaal ca. 116 x 135 cm.',
      },
      {
        name: 'Sign Again® 2 mm Recyclingoptik',
        description:
          'Sign Again® ist eine Platte aus 100 % recyceltem Material, hergestellt aus recyceltem Bannermaterial. Die Platte ist stark, schlag- und kratzfest und dennoch biegsam, schwer entflammbar nach EN 13501 (D-s3,d2) und damit auch für öffentliche Räume geeignet. Für innen und außen, mit einer erwarteten Lebensdauer von 2 bis 5 Jahren. Diese Ausführung hat eine unbehandelte Recyclingoptik; eine matte oder glänzende Schutzschicht ist optional. Stärke 2 mm, Gewicht ca. 2,8 kg/m2, maximal ca. 116 x 135 cm.',
      },
      {
        name: 'Sign Again® 2 mm recycled look',
        description:
          'Sign Again® is a sheet made from 100% recycled material, produced from recycled banner material. It is strong, impact- and scratch-resistant yet flexible, flame-retardant according to EN 13501 (D-s3,d2) and therefore also suitable for public spaces. Suitable for indoor and outdoor use, with an expected lifespan of 2 to 5 years. This version has an untreated recycled look; a matt or gloss protective coating is optional. Thickness 2 mm, weight approx. 2.8 kg/m2, maximum approx. 116 x 135 cm.',
      },
      {
        name: 'Sign Again® 2 mm aspect recyclé',
        description:
          'Sign Again® est une plaque en matière 100 % recyclée, fabriquée à partir de bâches recyclées. Elle est solide, résistante aux chocs et aux rayures tout en restant souple, ignifuge selon EN 13501 (D-s3,d2) et donc adaptée aussi aux espaces publics. Pour l’intérieur et l’extérieur, avec une durée de vie prévue de 2 à 5 ans. Cette version présente un aspect recyclé non traité ; un revêtement protecteur mat ou brillant est en option. Épaisseur 2 mm, poids env. 2,8 kg/m2, max. env. 116 x 135 cm.',
      }
    ),
  },
  {
    key: 'sign-again-2mm-white',
    aliases: [/\bsign\s*again(?:®)?\s*2\s*mm\s*(?:white|wit\b|weiß|blanc)/i],
    copy: copy(
      {
        name: 'Sign Again® 2 mm wit',
        description:
          'Sign Again® is een plaat van 100% gerecycled materiaal, gemaakt van gerecycled bannermateriaal. De plaat is sterk, slag- en krasvast en toch buigzaam, brandvertragend volgens EN 13501 (D-s3,d2) en daardoor ook geschikt voor openbare ruimtes. Geschikt voor binnen en buiten, met een verwachte levensduur van 2 tot 5 jaar. Deze uitvoering heeft een wit gecoate toplaag; een matte of glanzende beschermlaag is optioneel. Dikte 2 mm, gewicht ca. 2,8 kg/m2, maximaal ca. 116 x 135 cm.',
      },
      {
        name: 'Sign Again® 2 mm weiß',
        description:
          'Sign Again® ist eine Platte aus 100 % recyceltem Material, hergestellt aus recyceltem Bannermaterial. Die Platte ist stark, schlag- und kratzfest und dennoch biegsam, schwer entflammbar nach EN 13501 (D-s3,d2) und damit auch für öffentliche Räume geeignet. Für innen und außen, mit einer erwarteten Lebensdauer von 2 bis 5 Jahren. Diese Ausführung hat eine weiß beschichtete Oberfläche; eine matte oder glänzende Schutzschicht ist optional. Stärke 2 mm, Gewicht ca. 2,8 kg/m2, maximal ca. 116 x 135 cm.',
      },
      {
        name: 'Sign Again® 2 mm white',
        description:
          'Sign Again® is a sheet made from 100% recycled material, produced from recycled banner material. It is strong, impact- and scratch-resistant yet flexible, flame-retardant according to EN 13501 (D-s3,d2) and therefore also suitable for public spaces. Suitable for indoor and outdoor use, with an expected lifespan of 2 to 5 years. This version has a white coated surface; a matt or gloss protective coating is optional. Thickness 2 mm, weight approx. 2.8 kg/m2, maximum approx. 116 x 135 cm.',
      },
      {
        name: 'Sign Again® 2 mm blanc',
        description:
          'Sign Again® est une plaque en matière 100 % recyclée, fabriquée à partir de bâches recyclées. Elle est solide, résistante aux chocs et aux rayures tout en restant souple, ignifuge selon EN 13501 (D-s3,d2) et donc adaptée aussi aux espaces publics. Pour l’intérieur et l’extérieur, avec une durée de vie prévue de 2 à 5 ans. Cette version présente une surface blanche enduite ; un revêtement protecteur mat ou brillant est en option. Épaisseur 2 mm, poids env. 2,8 kg/m2, max. env. 116 x 135 cm.',
      }
    ),
  },
  {
    key: 'sign-again-4mm-recycled',
    aliases: [/\bsign\s*again(?:®)?\s*4\s*mm\s*(?:recycled|recycle-look|recyclingoptik|aspect\s*recycl)/i],
    copy: copy(
      {
        name: 'Sign Again® 4 mm recycle-look',
        description:
          'Sign Again® is een plaat van 100% gerecycled materiaal, gemaakt van gerecycled bannermateriaal. Deze 4 mm plaat is dikker en zwaarder dan de 2 mm uitvoering, sterk, slag- en krasvast en brandvertragend volgens EN 13501 (D-s3,d2), dus ook geschikt voor openbare ruimtes. Geschikt voor binnen en buiten, met een verwachte levensduur van 2 tot 5 jaar. Onbehandelde recycle-look; een matte of glanzende beschermlaag is optioneel. Dikte 4 mm, gewicht ca. 5,6 kg/m2, maximaal ca. 116 x 135 cm.',
      },
      {
        name: 'Sign Again® 4 mm Recyclingoptik',
        description:
          'Sign Again® ist eine Platte aus 100 % recyceltem Material, hergestellt aus recyceltem Bannermaterial. Diese 4-mm-Platte ist dicker und schwerer als die 2-mm-Ausführung, stark, schlag- und kratzfest und schwer entflammbar nach EN 13501 (D-s3,d2), also auch für öffentliche Räume geeignet. Für innen und außen, mit einer erwarteten Lebensdauer von 2 bis 5 Jahren. Unbehandelte Recyclingoptik; eine matte oder glänzende Schutzschicht ist optional. Stärke 4 mm, Gewicht ca. 5,6 kg/m2, maximal ca. 116 x 135 cm.',
      },
      {
        name: 'Sign Again® 4 mm recycled look',
        description:
          'Sign Again® is a sheet made from 100% recycled material, produced from recycled banner material. This 4 mm sheet is thicker and heavier than the 2 mm version, strong, impact- and scratch-resistant and flame-retardant according to EN 13501 (D-s3,d2), so it is also suitable for public spaces. Suitable for indoor and outdoor use, with an expected lifespan of 2 to 5 years. Untreated recycled look; a matt or gloss protective coating is optional. Thickness 4 mm, weight approx. 5.6 kg/m2, maximum approx. 116 x 135 cm.',
      },
      {
        name: 'Sign Again® 4 mm aspect recyclé',
        description:
          'Sign Again® est une plaque en matière 100 % recyclée, fabriquée à partir de bâches recyclées. Cette plaque de 4 mm est plus épaisse et plus lourde que la version de 2 mm, solide, résistante aux chocs et aux rayures et ignifuge selon EN 13501 (D-s3,d2), donc adaptée aussi aux espaces publics. Pour l’intérieur et l’extérieur, avec une durée de vie prévue de 2 à 5 ans. Aspect recyclé non traité ; un revêtement protecteur mat ou brillant est en option. Épaisseur 4 mm, poids env. 5,6 kg/m2, max. env. 116 x 135 cm.',
      }
    ),
  },
  {
    key: 'forex-1mm',
    aliases: [/\b1\s*mm\s*forex\b/i, /\b1mm\s*forex\b/i, /\b1\s*mm\s*foamed\s*pvc\b/i],
    copy: copy(
      {
        name: '1 mm Forex®',
        description:
          'Forex® is gemaakt van geschuimd PVC. Het materiaal is licht van gewicht, sterk door de geschuimde kern en relatief flexibel. De witte matte toplaag is licht gevoelig voor krassen, maar de inkt hecht goed. Brandvertragend en waterafstotend, geschikt voor binnen en tijdelijk buitengebruik. Dikte 1 mm, gewicht ca. 0,68 kg/m2, maximale afmeting ca. 301 x 152 cm.',
      },
      {
        name: '1 mm Forex®',
        description:
          'Forex® besteht aus geschaeumtem PVC. Das Material ist leicht, durch den Schaumkern stabil und relativ flexibel. Die matte weisse Oberfläche ist leicht kratzempfindlich, die Tinte haftet jedoch gut. Schwer entflammbar und wasserabweisend, für innen und zeitweise aussen geeignet. Stärke 1 mm, Gewicht ca. 0,68 kg/m2, Maximalformat ca. 301 x 152 cm.',
      },
      {
        name: '1 mm Forex®',
        description:
          'Forex® is made from foamed PVC. It is lightweight, strong thanks to the foam core and relatively flexible. The matte white surface is somewhat scratch-sensitive, but ink adhesion is good. Flame-retardant and water-repellent, suitable for indoor and temporary outdoor use. Thickness 1 mm, weight approx. 0.68 kg/m2, maximum size approx. 301 x 152 cm.',
      },
      {
        name: 'Forex® 1 mm',
        description:
          'Forex® est fabrique en PVC expanse. Le matériau est léger, solide grace au coeur mousse et relativement flexible. La surface blanche mate est légèrement sensible aux rayures, mais l’encre adhere bien. Ignifuge et hydrofuge, adapte à l’intérieur et à un usage extérieur temporaire. Épaisseur 1 mm, poids env. 0,68 kg/m2, format max. env. 301 x 152 cm.',
      }
    ),
  },
  {
    key: 'forex-3mm-re',
    aliases: [/\b3\s*mm\s*forex(?:®)?\s*re\b/i, /\b3mm\s*forex(?:®)?\s*re\b/i, /\b3\s*mm\s*forexre\b/i, /\b3mm\s*forexre\b/i],
    copy: copy(
      {
        name: '3 mm Forex®re',
        description:
          'Forex®re bestaat voor 30% uit gerecycled Forex® en heeft een subtiele gerecyclede uitstraling. Licht, sterk door de geschuimde kern en relatief flexibel. Brandvertragend en waterafstotend, geschikt voor binnen en buiten. Dikte 3 mm, gewicht ca. 1,73 kg/m2, maximale afmeting ca. 301 x 152 cm.',
      },
      {
        name: '3 mm Forex®re',
        description:
          'Forex®re besteht zu 30% aus recyceltem Forex® und hat eine dezente Recycling-Optik. Leicht, durch den Schaumkern stabil und relativ flexibel. Schwer entflammbar und wasserabweisend, für innen und aussen geeignet. Stärke 3 mm, Gewicht ca. 1,73 kg/m2, Maximalformat ca. 301 x 152 cm.',
      },
      {
        name: '3 mm Forex®re',
        description:
          'Forex®re contains 30% recycled Forex® and has a subtle recycled look. Lightweight, strong thanks to the foam core and relatively flexible. Flame-retardant and water-repellent, suitable for indoor and outdoor use. Thickness 3 mm, weight approx. 1.73 kg/m2, maximum size approx. 301 x 152 cm.',
      },
      {
        name: 'Forex®re 3 mm',
        description:
          'Forex®re contient 30% de Forex® recycle et presente un aspect recycle subtil. Leger, solide grace au coeur mousse et relativement flexible. Ignifuge et hydrofuge, adapte a l interieur et a l exterieur. Epaisseur 3 mm, poids env. 1,73 kg/m2, format max. env. 301 x 152 cm.',
      }
    ),
  },
  {
    key: 'forex-3mm',
    aliases: [/\b3\s*mm\s*forex\b/i, /\b3mm\s*forex\b/i, /\b3\s*mm\s*foamed\s*pvc\b/i],
    copy: copy(
      {
        name: '3 mm Forex®',
        description:
          'Forex® is gemaakt van geschuimd PVC. Het materiaal is licht van gewicht, sterk door de geschuimde kern en relatief flexibel. De witte matte toplaag is licht gevoelig voor krassen, maar de inkt hecht goed. Brandvertragend en waterafstotend, geschikt voor binnen en buiten. Dikte 3 mm, gewicht ca. 1,73 kg/m2, maximale afmeting ca. 301 x 199 cm.',
      },
      {
        name: '3 mm Forex®',
        description:
          'Forex® besteht aus geschaeumtem PVC. Leicht, durch den Schaumkern stabil und relativ flexibel. Die matte weisse Oberfläche ist leicht kratzempfindlich, die Tinte haftet jedoch gut. Schwer entflammbar und wasserabweisend, für innen und aussen geeignet. Stärke 3 mm, Gewicht ca. 1,73 kg/m2, Maximalformat ca. 301 x 199 cm.',
      },
      {
        name: '3 mm Forex®',
        description:
          'Forex® is made from foamed PVC. It is lightweight, strong thanks to the foam core and relatively flexible. The matte white surface is somewhat scratch-sensitive, but ink adhesion is good. Flame-retardant and water-repellent, suitable for indoor and outdoor use. Thickness 3 mm, weight approx. 1.73 kg/m2, maximum size approx. 301 x 199 cm.',
      },
      {
        name: 'Forex® 3 mm',
        description:
          'Forex® est fabrique en PVC expanse. Leger, solide grace au coeur mousse et relativement flexible. La surface blanche mate est légèrement sensible aux rayures, mais l’encre adhere bien. Ignifuge et hydrofuge, adapte à l’intérieur et à l’extérieur. Épaisseur 3 mm, poids env. 1,73 kg/m2, format max. env. 301 x 199 cm.',
      }
    ),
  },
  {
    key: 'forex-5mm-re',
    aliases: [/\b5\s*mm\s*forex(?:®)?\s*re\b/i, /\b5mm\s*forex(?:®)?\s*re\b/i, /\b5\s*mm\s*forexre\b/i, /\b5mm\s*forexre\b/i],
    copy: copy(
      {
        name: '5 mm Forex®re',
        description:
          'Forex®re bestaat voor 30% uit gerecycled Forex® en heeft een subtiele gerecyclede uitstraling. Licht, sterk door de geschuimde kern en relatief flexibel. Brandvertragend en waterafstotend, geschikt voor binnen en buiten. Dikte 5 mm, gewicht ca. 2,2 kg/m2, maximale afmeting ca. 301 x 152 cm.',
      },
      {
        name: '5 mm Forex®re',
        description:
          'Forex®re besteht zu 30% aus recyceltem Forex® und hat eine dezente Recycling-Optik. Leicht, durch den Schaumkern stabil und relativ flexibel. Schwer entflammbar und wasserabweisend, für innen und aussen geeignet. Stärke 5 mm, Gewicht ca. 2,2 kg/m2, Maximalformat ca. 301 x 152 cm.',
      },
      {
        name: '5 mm Forex®re',
        description:
          'Forex®re contains 30% recycled Forex® and has a subtle recycled look. Lightweight, strong thanks to the foam core and relatively flexible. Flame-retardant and water-repellent, suitable for indoor and outdoor use. Thickness 5 mm, weight approx. 2.2 kg/m2, maximum size approx. 301 x 152 cm.',
      },
      {
        name: 'Forex®re 5 mm',
        description:
          'Forex®re contient 30% de Forex® recycle et presente un aspect recycle subtil. Leger, solide grace au coeur mousse et relativement flexible. Ignifuge et hydrofuge, adapte a l interieur et a l exterieur. Epaisseur 5 mm, poids env. 2,2 kg/m2, format max. env. 301 x 152 cm.',
      }
    ),
  },
  {
    key: 'forex-5mm',
    aliases: [/\b5\s*mm\s*forex\b/i, /\b5mm\s*forex\b/i, /\b5\s*mm\s*foamed\s*pvc\b/i],
    copy: copy(
      {
        name: '5 mm Forex®',
        description:
          'Forex® is gemaakt van geschuimd PVC. Licht, sterk door de geschuimde kern en relatief flexibel. De witte matte toplaag is licht gevoelig voor krassen, maar de inkt hecht goed. Brandvertragend en waterafstotend, geschikt voor binnen en buiten. Dikte 5 mm, gewicht ca. 2,2 kg/m2, maximale afmeting ca. 301 x 199 cm.',
      },
      {
        name: '5 mm Forex®',
        description:
          'Forex® besteht aus geschaeumtem PVC. Leicht, durch den Schaumkern stabil und relativ flexibel. Die matte weisse Oberfläche ist leicht kratzempfindlich, die Tinte haftet jedoch gut. Schwer entflammbar und wasserabweisend, für innen und aussen geeignet. Stärke 5 mm, Gewicht ca. 2,2 kg/m2, Maximalformat ca. 301 x 199 cm.',
      },
      {
        name: '5 mm Forex®',
        description:
          'Forex® is made from foamed PVC. Lightweight, strong thanks to the foam core and relatively flexible. The matte white surface is somewhat scratch-sensitive, but ink adhesion is good. Flame-retardant and water-repellent, suitable for indoor and outdoor use. Thickness 5 mm, weight approx. 2.2 kg/m2, maximum size approx. 301 x 199 cm.',
      },
      {
        name: 'Forex® 5 mm',
        description:
          'Forex® est fabrique en PVC expanse. Leger, solide grace au coeur mousse et relativement flexible. La surface blanche mate est légèrement sensible aux rayures, mais l’encre adhere bien. Ignifuge et hydrofuge, adapte à l’intérieur et à l’extérieur. Épaisseur 5 mm, poids env. 2,2 kg/m2, format max. env. 301 x 199 cm.',
      }
    ),
  },
  {
    key: 'forex-10mm',
    aliases: [/\b10\s*mm\s*forex\b/i, /\b10mm\s*forex\b/i, /\b10\s*mm\s*foamed\s*pvc\b/i],
    copy: copy(
      {
        name: '10 mm Forex®',
        description:
          'Forex® is gemaakt van geschuimd PVC. Deze 10 mm plaat is extra stevig en robuust, maar blijft relatief licht. De witte matte toplaag is licht gevoelig voor krassen, maar de inkt hecht goed. Brandvertragend en waterafstotend, geschikt voor binnen en buiten. Gewicht ca. 4,50 kg/m2, maximale afmeting ca. 301 x 152 cm.',
      },
      {
        name: '10 mm Forex®',
        description:
          'Forex® besteht aus geschaeumtem PVC. Diese 10-mm-Platte ist extra stabil und robust, bleibt aber relativ leicht. Die matte weisse Oberfläche ist leicht kratzempfindlich, die Tinte haftet jedoch gut. Schwer entflammbar und wasserabweisend, für innen und aussen geeignet. Gewicht ca. 4,50 kg/m2, Maximalformat ca. 301 x 152 cm.',
      },
      {
        name: '10 mm Forex®',
        description:
          'Forex® is made from foamed PVC. This 10 mm sheet is extra sturdy and robust while remaining relatively lightweight. The matte white surface is somewhat scratch-sensitive, but ink adhesion is good. Flame-retardant and water-repellent, suitable for indoor and outdoor use. Weight approx. 4.50 kg/m2, maximum size approx. 301 x 152 cm.',
      },
      {
        name: 'Forex® 10 mm',
        description:
          'Forex® est fabrique en PVC expanse. Cette plaque de 10 mm est extra rigide et robuste tout en restant relativement légère. La surface blanche mate est légèrement sensible aux rayures, mais l’encre adhere bien. Ignifuge et hydrofuge, adapte à l’intérieur et à l’extérieur. Poids env. 4,50 kg/m2, format max. env. 301 x 152 cm.',
      }
    ),
  },
  {
    key: 'gesatineerd-mc',
    aliases: [/gesatineerd[_\s-]*mc/i, /glanzend papier/i, /glossy paper/i],
    copy: copy(
      {
        name: 'Gesatineerd MC',
        description:
          'Glad, glanzend papier waarop kleuren en fotodetails sterk uitkomen. Een goede keuze voor beeldrijk drukwerk. Houd rekening met meer lichtreflectie bij veel tekst.',
      },
      {
        name: 'Satiniertes MC',
        description:
          'Glattes, glänzendes Papier mit kräftiger Farb- und Fotowiedergabe. Eine gute Wahl für bildstarke Drucksachen. Bei viel Text kann die Reflexion stören.',
      },
      {
        name: 'Gloss coated paper',
        description:
          'Smooth glossy paper with strong colour and photo detail. A good choice for image-heavy print. The reflective surface can reduce readability for text-heavy designs.',
      },
      {
        name: 'Papier couche brillant',
        description:
          'Papier lisse et brillant avec un bon rendu des couleurs et des photos. Idéal pour les visuels riches. La brillance peut gener la lecture des textes longs.',
      }
    ),
  },
  {
    key: 'silk-mc',
    aliases: [/silk[_\s-]*mc/i, /zijdeglans/i, /zijde\s*mat/i, /silk/i],
    copy: copy(
      {
        name: 'Silk MC',
        description:
          'Zijdemat papier met een zachte uitstraling en goede tekstweergave. Afbeeldingen komen mooi uit, maar zonder laminaat is het gevoeliger voor krassen en smetten.',
      },
      {
        name: 'Silk MC',
        description:
          'Seidenmattes Papier mit weicher Wirkung und guter Lesbarkeit. Bilder wirken hochwertig, ohne Laminat ist die Oberfläche jedoch empfindlicher.',
      },
      {
        name: 'Silk coated paper',
        description:
          'Silk-matt paper with a soft look and good text readability. Images reproduce well, but without laminate the surface is more sensitive to scratches and marks.',
      },
      {
        name: 'Papier couche silk',
        description:
          'Papier satine au rendu doux et lisible. Les images ressortent bien, mais sans pelliculage la surface est plus sensible aux rayures et aux traces.',
      }
    ),
  },
  {
    key: 'kraft-muskat',
    aliases: [/muskat/i, /kraft/i, /brown kraft/i],
    copy: copy(
      {
        name: 'Kraftpapier Muskat bruin',
        description:
          'Bruin kraftpapier van gerecyclede vezels met een stoere, natuurlijke vezelstructuur. Ook geschikt voor witte inkt. Kleuren vallen donkerder uit op de bruine ondergrond.',
      },
      {
        name: 'Kraftpapier Muskat braun',
        description:
          'Braunes Kraftpapier aus Recyclingfasern mit robuster, natürlicher Faserstruktur. Auch für weiße Farbe geeignet. Farben wirken auf dem braunen Untergrund dunkler.',
      },
      {
        name: 'Muskat brown kraft paper',
        description:
          'Brown recycled kraft paper with a bold natural fibre texture. Also suitable for white ink. Colours appear darker on the brown base.',
      },
      {
        name: 'Papier kraft brun Muskat',
        description:
          'Papier kraft brun recycle avec une texture fibreuse naturelle. Convient aussi à l’encre blanche. Les couleurs paraissent plus foncees sur le fond brun.',
      }
    ),
  },
  {
    key: 'balancepure',
    aliases: [/balance\s*pure/i, /balancepure/i],
    copy: copy(
      {
        name: 'BalancePure recycled',
        description:
          'Wit, ongestreken papier van 100% gerecyclede vezels met een zachte, gladde touch. Goed beschrijfbaar en geschikt voor een rustige duurzame uitstraling.',
      },
      {
        name: 'BalancePure Recyclingpapier',
        description:
          'Weißes, ungestrichenes Papier aus 100% Recyclingfasern mit weicher, glatter Haptik. Gut beschreibbar und ideal für eine ruhige nachhaltige Wirkung.',
      },
      {
        name: 'BalancePure recycled paper',
        description:
          'White uncoated paper made from 100% recycled fibres with a soft, smooth feel. Easy to write on and suitable for a calm sustainable look.',
      },
      {
        name: 'Papier recycle BalancePure',
        description:
          'Papier blanc non couche compose de fibres 100% recyclees, doux et lisse. Facile a ecrire et ideal pour un rendu durable et sobre.',
      }
    ),
  },
  {
    key: 'recystar-nature',
    aliases: [/recystar/i, /naturekarton/i, /nature card/i],
    copy: copy(
      {
        name: 'RecyStar Nature',
        description:
          'Natuurwit, ongestreken papier van 100% gerecyclede vezels met een duidelijke eco-uitstraling. Goed beschrijfbaar en licht creme van kleur.',
      },
      {
        name: 'RecyStar Nature',
        description:
          'Naturweißes, ungestrichenes Papier aus 100% Recyclingfasern mit klarer Eco-Optik. Gut beschreibbar und leicht cremefarben.',
      },
      {
        name: 'RecyStar Nature',
        description:
          'Natural white uncoated paper made from 100% recycled fibres with a clear eco look. Easy to write on and slightly cream-toned.',
      },
      {
        name: 'RecyStar Nature',
        description:
          'Papier naturel non couche compose de fibres 100% recyclées, avec une vraie apparence eco. Facile a écrire et légèrement creme.',
      }
    ),
  },
  {
    key: 'hv-offset',
    aliases: [/^((?!natuurkarton).)*hv[o]?\s*offset/i, /^((?!natuurkarton).)*offset$/i],
    copy: copy(
      {
        name: 'HV offset',
        description:
          'Houtvrij ongestreken papier met een licht ruwe structuur en uitstekende beschrijfbaarheid. Kleuren ogen matter door de open papierstructuur.',
      },
      {
        name: 'Offsetpapier holzfrei',
        description:
          'Ungestrichenes holzfreies Papier mit leicht rauer Struktur und sehr guter Beschreibbarkeit. Farben wirken durch die offene Struktur matter.',
      },
      {
        name: 'Uncoated offset paper',
        description:
          'Woodfree uncoated paper with a slightly rough surface and excellent writability. Colours look more matte because of the open paper structure.',
      },
      {
        name: 'Papier offset non couche',
        description:
          'Papier offset non couche avec une surface légèrement rugueuse et très facile a écrire. Les couleurs paraissent plus mates.',
      }
    ),
  },
  {
    key: 'hvo-natuurkarton',
    aliases: [/hvo?[_\s-]*offset[_\s-]*natuurkarton/i, /natuurkarton/i],
    copy: copy(
      {
        name: 'HVO offset natuurkarton',
        description:
          'Compact, stevig en hoogwit natuurkarton met een glad, goed beschrijfbaar oppervlak. Door de hoge papierdichtheid heeft de bedrukking meer droogtijd nodig.',
      },
      {
        name: 'HVO Offset Naturkarton',
        description:
          'Kompakter, stabiler und hochweißer Naturkarton mit glatter, gut beschreibbarer Oberfläche. Durch die hohe Dichte braucht der Druck mehr Trockenzeit.',
      },
      {
        name: 'HVO offset natural card',
        description:
          'Compact, sturdy and bright white natural card with a smooth writable surface. The dense paper needs more drying time after printing.',
      },
      {
        name: 'Carton naturel offset HVO',
        description:
          'Carton naturel compact, rigide et blanc avec une surface lisse facile a écrire. Sa forte densite demande plus de temps de sechage.',
      }
    ),
  },
  {
    key: 'symbol-card',
    aliases: [/symbol[\s_-]*card/i, /sbs[\s_-]*symbol[\s_-]*card/i],
    copy: copy(
      {
        name: 'Symbol Card',
        description:
          'Stevig karton met een zachte strijklaag aan één kant en een goed beschrijfbare ongestreken achterkant. Geschikt voor kaarten; enkelzijdig lamineren kan het karton kromtrekken.',
      },
      {
        name: 'Symbol Card',
        description:
          'Stabiler Karton mit einer weichen Beschichtung auf einer Seite und einer gut beschreibbaren ungestrichenen Rückseite. Geeignet für Karten; einseitiges Laminieren kann den Karton verziehen.',
      },
      {
        name: 'Symbol Card',
        description:
          'Stiff card with a soft coating on one side and an uncoated reverse that is easy to write on. Suitable for cards; laminating only one side may cause it to warp.',
      },
      {
        name: 'Symbol Card',
        description:
          'Carton rigide avec une face légèrement couchée et un verso non couché facile à écrire. Adapté aux cartes ; un pelliculage sur une seule face peut le déformer.',
      }
    ),
  },
  {
    key: 'multicard',
    aliases: [/multicard/i, /sulfaatkarton/i, /sbs/i, /sbd/i],
    copy: copy(
      {
        name: 'MultiCard sulfaatkarton',
        description:
          'Meervoudig gestreken sulfaatkarton met hoge stijfheid en realistische kleurweergave. Voelt steviger aan dan veel papiersoorten met hetzelfde gramgewicht.',
      },
      {
        name: 'MultiCard Sulfatkarton',
        description:
          'Mehrlagiger gestrichener Sulfatkarton mit hoher Steifigkeit und realistischer Farbwiedergabe. Fühlt sich stabiler an als viele Papiere mit gleichem Gewicht.',
      },
      {
        name: 'MultiCard sulphate board',
        description:
          'Multi-layer coated sulphate board with high stiffness and realistic colour reproduction. Feels sturdier than many papers with the same weight.',
      },
      {
        name: 'Carton sulfate MultiCard',
        description:
          'Carton sulfate multicouche couche avec une forte rigidite et un rendu couleur realiste. Plus rigide que beaucoup de papiers du même grammage.',
      }
    ),
  },
  {
    key: 'sirio-metallic',
    aliases: [/sirio.*metallic/i, /metallic.*(wit|white|zilver|silver|goud|gold|parel|pearl)/i],
    copy: copy(
      {
        name: 'Sirio metallic',
        description:
          'Opvallend metallic papier voor bijzondere gelegenheden. Zilver, goud, parelmoer of wit geeft drukwerk direct een premium glans. Lichte kleuren kunnen minder helder ogen.',
      },
      {
        name: 'Sirio Metallic',
        description:
          'Auffälliges Metallic-Papier für besondere Anlässe. Silber, Gold, Perlmut oder Weiß sorgt sofort für Premium-Glanz. Helle Farben können weniger klar wirken.',
      },
      {
        name: 'Sirio metallic paper',
        description:
          'Striking metallic paper for special occasions. Silver, gold, pearl or white gives print a premium shimmer. Light colours can appear less bright.',
      },
      {
        name: 'Papier metallise Sirio',
        description:
          'Papier metallise remarquable pour occasions speciales. Argent, or, nacre ou blanc donne un rendu premium. Les couleurs claires peuvent etre moins vives.',
      }
    ),
  },
  {
    key: 'sirio-color',
    aliases: [/sirio.*(color|nero|pietra)/i],
    copy: copy(
      {
        name: 'Sirio Color',
        description:
          'Gekleurd premium papier met een licht ruwe touch. Sterk voor minimale ontwerpen en witte bedrukking, omdat de papierkleur zelf al opvalt.',
      },
      {
        name: 'Sirio Color',
        description:
          'Farbiges Premiumpapier mit leicht rauer Haptik. Stark für minimale Designs und weißen Druck, da die Papierfarbe selbst auffällt.',
      },
      {
        name: 'Sirio Color paper',
        description:
          'Coloured premium paper with a slightly rough touch. Strong for minimal designs and white ink because the paper colour itself stands out.',
      },
      {
        name: 'Papier Sirio Color',
        description:
          'Papier premium colore au toucher légèrement rugueux. Idéal pour designs minimalistes et impression blanche, car la couleur du papier attire déjà l’oeil.',
      }
    ),
  },
  {
    key: 'yupoblue',
    aliases: [/yupo\s*blue/i, /yupoblue/i],
    copy: copy(
      {
        name: 'YUPOBlue waterproof',
        description:
          'Sterk kunststof materiaal dat waterbestendig en scheurvast is. Geschikt voor intensief gebruik, vochtige ruimtes en toepassingen die langer mee moeten.',
      },
      {
        name: 'YUPOBlue wasserfest',
        description:
          'Starkes Kunststoffmaterial, wasserfest und reißfest. Geeignet für intensive Nutzung, feuchte Räume und langlebige Anwendungen.',
      },
      {
        name: 'YUPOBlue waterproof',
        description:
          'Strong synthetic material that is waterproof and tear-resistant. Suitable for heavy use, humid spaces and applications that need to last.',
      },
      {
        name: 'YUPOBlue waterproof',
        description:
          'Materiau synthetique solide, résistant à l’eau et à la dechirure. Convient à un usage intensif, aux lieux humides et aux applications durables.',
      }
    ),
  },
  {
    key: 'constellation-snow',
    aliases: [/constellation/i, /linnenpersing/i, /linen/i],
    copy: copy(
      {
        name: 'Constellation Snow linnenpersing',
        description:
          'Hoogwit papier met subtiel lijnrelief voor een klassieke, chique uitstraling. De structuur blijft zichtbaar in de bedrukking.',
      },
      {
        name: 'Constellation Snow Leinenpraegung',
        description:
          'Hochweißes Papier mit feinem Linienrelief für eine klassische, edle Wirkung. Die Struktur bleibt im Druck sichtbar.',
      },
      {
        name: 'Constellation Snow linen texture',
        description:
          'Bright white paper with a subtle linen line texture for a classic premium look. The texture remains visible in the print.',
      },
      {
        name: 'Constellation Snow texture lin',
        description:
          'Papier très blanc avec un relief ligne subtil pour un rendu classique et chic. La texture reste visible dans l’impression.',
      }
    ),
  },
  {
    key: 'conqueror-verge',
    aliases: [/conqueror/i, /verge/i, /verg[eé]/i],
    copy: copy(
      {
        name: 'Conqueror verge',
        description:
          'Stijlvol papier met herkenbare parallelle lijnen. Geeft drukwerk een klassieke uitstraling; foto en kleine details kunnen door de structuur minder scherp ogen.',
      },
      {
        name: 'Conqueror gerippt',
        description:
          'Stilvolles Papier mit erkennbaren parallelen Linien. Klassische Wirkung; Fotos und kleine Details können durch die Struktur weniger scharf wirken.',
      },
      {
        name: 'Conqueror laid paper',
        description:
          'Stylish paper with a recognisable parallel line pattern. Gives a classic look; photos and fine details may appear less sharp.',
      },
      {
        name: 'Papier verge Conqueror',
        description:
          'Papier elegant avec lignes paralleles visibles. Donne un rendu classique; photos et petits details peuvent paraitre moins nets.',
      }
    ),
  },
  {
    key: 'tintoretto-gesso',
    aliases: [/tintoretto/i, /gesso/i, /gehamerd/i],
    copy: copy(
      {
        name: 'Tintoretto Gesso gehamerd',
        description:
          'Gehamerd papier met organische structuur en lichte welvingen. Geeft drukwerk een schilderachtige uitstraling; de bedrukking oogt niet volledig dekkend.',
      },
      {
        name: 'Tintoretto Gesso gehammert',
        description:
          'Gehammertes Papier mit organischer Struktur und leichten Wellen. Sorgt für malerische Wirkung; der Druck wirkt nicht vollständig deckend.',
      },
      {
        name: 'Tintoretto Gesso hammered',
        description:
          'Hammered paper with an organic texture and subtle waves. Creates an artistic look; the print does not appear fully opaque.',
      },
      {
        name: 'Tintoretto Gesso martel',
        description:
          'Papier martele avec texture organique et leger relief. Donne un rendu artistique; l impression parait moins totalement couvrante.',
      }
    ),
  },
  {
    key: 'king-board-white',
    aliases: [/king\s*board/i, /verfijnd karton/i],
    copy: copy(
      {
        name: 'King board white verfijnd karton',
        description:
          'Naturel karton met een verfijnd, katoenachtig papiergevoel en robuuste uitstraling. Sterke kleurweergave en geschikt voor folies zonder laminaat.',
      },
      {
        name: 'King board white Feinkarton',
        description:
          'Natürlicher Karton mit feinem, baumwollartigem Papiergefühl und robuster Wirkung. Gute Farbwiedergabe und für Folien ohne Laminat geeignet.',
      },
      {
        name: 'King board white refined card',
        description:
          'Natural refined card with a cotton-like paper feel and robust look. Strong colour reproduction and suitable for foil finishing without laminate.',
      },
      {
        name: 'King board white carton raffine',
        description:
          'Carton naturel raffine avec un toucher proche du coton et une apparence robuste. Bon rendu couleur et compatible avec dorure sans pelliculage.',
      }
    ),
  },
];

function language(locale: string): PrintMaterialLocale {
  const lang = locale.split('-')[0] as PrintMaterialLocale;
  return ['nl', 'de', 'en', 'fr'].includes(lang) ? lang : 'nl';
}

function sourceText(material: { value?: unknown; label?: unknown; id?: unknown; name?: unknown }) {
  return [material.value, material.label, material.id, material.name]
    .filter((part) => part !== undefined && part !== null)
    .map(String)
    .join(' ')
    .toLowerCase()
    .replace(/_/g, ' ');
}

export function getPrintMaterialInfo(
  material: { value?: unknown; label?: unknown; id?: unknown; name?: unknown },
  locale = 'nl'
): PrintMaterialInfo | null {
  if (!['nl', 'de', 'en', 'fr'].includes(locale.split('-')[0])) return null;
  const text = sourceText(material);
  const match = MATERIALS.find((entry) => entry.aliases.some((alias) => alias.test(text)));
  if (!match) return null;

  const localized = match.copy[language(locale)] || match.copy.nl;
  return {
    key: match.key,
    name: localized.name,
    description: localized.description,
  };
}
